import React, { useState, useEffect } from 'react';
import { Coins, Plus, Pencil, Trash2, X, GripVertical, ToggleLeft, ToggleRight, ExternalLink } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface CoinPackage {
  id: string;
  name: string;
  coin_amount: number;
  price_cents: number;
  stripe_product_id: string;
  stripe_price_id: string;
  tag: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PackageForm {
  name: string;
  coin_amount: number;
  price_cents: number;
  stripe_product_id: string;
  stripe_price_id: string;
  tag: string;
  sort_order: number;
  is_active: boolean;
}

const emptyForm: PackageForm = {
  name: '',
  coin_amount: 100,
  price_cents: 199,
  stripe_product_id: '',
  stripe_price_id: '',
  tag: '',
  sort_order: 0,
  is_active: true,
};

export const AdminCoinPackages: React.FC = () => {
  const [packages, setPackages] = useState<CoinPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<CoinPackage | null>(null);
  const [form, setForm] = useState<PackageForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('coin_packages')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setPackages(data || []);
    } catch (err) {
      console.error('Error loading packages:', err);
      setError('Failed to load coin packages');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingPackage(null);
    setForm({
      ...emptyForm,
      sort_order: packages.length,
    });
    setShowModal(true);
    setError(null);
  };

  const openEditModal = (pkg: CoinPackage) => {
    setEditingPackage(pkg);
    setForm({
      name: pkg.name,
      coin_amount: pkg.coin_amount,
      price_cents: pkg.price_cents,
      stripe_product_id: pkg.stripe_product_id,
      stripe_price_id: pkg.stripe_price_id,
      tag: pkg.tag || '',
      sort_order: pkg.sort_order,
      is_active: pkg.is_active,
    });
    setShowModal(true);
    setError(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Package name is required');
      return;
    }
    if (form.coin_amount <= 0) {
      setError('Coin amount must be greater than 0');
      return;
    }
    if (form.price_cents <= 0) {
      setError('Price must be greater than 0');
      return;
    }
    if (!form.stripe_product_id.trim()) {
      setError('Stripe Product ID is required');
      return;
    }
    if (!form.stripe_price_id.trim()) {
      setError('Stripe Price ID is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: form.name.trim(),
        coin_amount: form.coin_amount,
        price_cents: form.price_cents,
        stripe_product_id: form.stripe_product_id.trim(),
        stripe_price_id: form.stripe_price_id.trim(),
        tag: form.tag.trim() || null,
        sort_order: form.sort_order,
        is_active: form.is_active,
        updated_at: new Date().toISOString(),
      };

      if (editingPackage) {
        const { error } = await supabase
          .from('coin_packages')
          .update(payload)
          .eq('id', editingPackage.id);
        if (error) throw error;
        setSuccess('Package updated successfully');
      } else {
        const { error } = await supabase
          .from('coin_packages')
          .insert([payload]);
        if (error) throw error;
        setSuccess('Package created successfully');
      }

      setShowModal(false);
      await loadPackages();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving package:', err);
      setError(err instanceof Error ? err.message : 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('coin_packages')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setShowDeleteConfirm(null);
      setSuccess('Package deleted successfully');
      await loadPackages();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting package:', err);
      setError('Failed to delete package');
    }
  };

  const toggleActive = async (pkg: CoinPackage) => {
    try {
      const { error } = await supabase
        .from('coin_packages')
        .update({ is_active: !pkg.is_active, updated_at: new Date().toISOString() })
        .eq('id', pkg.id);
      if (error) throw error;
      await loadPackages();
    } catch (err) {
      console.error('Error toggling package:', err);
      setError('Failed to update package status');
    }
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div>
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4 text-red-500 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-4 text-green-500 text-sm">
          {success}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Coin Packages</h3>
        <div className="flex items-center gap-3">
          <a
            href="https://dashboard.stripe.com/products"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-kod-purple-700 hover:bg-kod-purple-600 text-white px-4 py-2 rounded-lg flex items-center text-sm transition-colors"
          >
            <ExternalLink size={16} className="mr-2" />
            Manage in Stripe
          </a>
          <button
            onClick={openCreateModal}
            className="bg-kod-accent hover:bg-kod-accent-dark text-white px-4 py-2 rounded-lg flex items-center text-sm transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Package
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent"></div>
        </div>
      ) : packages.length === 0 ? (
        <div className="text-center py-10 text-white/50">
          <Coins size={32} className="mx-auto mb-3 opacity-50" />
          <p>No coin packages configured yet.</p>
          <p className="text-sm mt-1">Add your first package to get started.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-kod-purple-600">
                <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Order</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Coins</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Stripe Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Tag</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kod-purple-600/50">
              {packages.map((pkg) => (
                <tr key={pkg.id} className="hover:bg-kod-purple-700/50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center text-white/50">
                      <GripVertical size={14} className="mr-1" />
                      {pkg.sort_order}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap font-medium">{pkg.name}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Coins size={14} className="text-yellow-400 mr-1.5" />
                      {pkg.coin_amount.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap font-medium text-green-400">
                    {formatPrice(pkg.price_cents)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      {pkg.stripe_product_id ? (
                        <a
                          href={`https://dashboard.stripe.com/products/${pkg.stripe_product_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-kod-accent hover:text-kod-accent-dark font-mono truncate max-w-[160px] inline-flex items-center gap-1"
                          title={pkg.stripe_product_id}
                        >
                          {pkg.stripe_product_id}
                          <ExternalLink size={10} className="flex-shrink-0" />
                        </a>
                      ) : (
                        <span className="text-xs text-white/50">-</span>
                      )}
                      {pkg.stripe_price_id ? (
                        <a
                          href={`https://dashboard.stripe.com/prices/${pkg.stripe_price_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-white/40 hover:text-white/60 font-mono truncate max-w-[160px] inline-flex items-center gap-1"
                          title={pkg.stripe_price_id}
                        >
                          {pkg.stripe_price_id}
                          <ExternalLink size={10} className="flex-shrink-0" />
                        </a>
                      ) : (
                        <span className="text-xs text-white/30">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {pkg.tag ? (
                      <span className="bg-kod-accent/20 text-kod-accent text-xs px-2 py-1 rounded-full font-medium">
                        {pkg.tag}
                      </span>
                    ) : (
                      <span className="text-white/30 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleActive(pkg)}
                      className="flex items-center gap-1.5"
                      title={pkg.is_active ? 'Click to deactivate' : 'Click to activate'}
                    >
                      {pkg.is_active ? (
                        <>
                          <ToggleRight size={20} className="text-green-400" />
                          <span className="text-xs text-green-400">Active</span>
                        </>
                      ) : (
                        <>
                          <ToggleLeft size={20} className="text-white/30" />
                          <span className="text-xs text-white/30">Inactive</span>
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditModal(pkg)}
                        className="p-1.5 text-white/50 hover:text-white transition-colors rounded hover:bg-kod-purple-600"
                        title="Edit package"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(pkg.id)}
                        className="p-1.5 text-white/50 hover:text-red-400 transition-colors rounded hover:bg-red-500/10"
                        title="Delete package"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-xl p-6 max-w-lg w-full shadow-2xl border border-kod-purple-600/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">
                {editingPackage ? 'Edit Coin Package' : 'New Coin Package'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-white/50 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Package Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  placeholder="e.g., Starter Pack"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Coin Amount</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                    value={form.coin_amount}
                    onChange={(e) => setForm(prev => ({ ...prev, coin_amount: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Price (cents)</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                      value={form.price_cents}
                      onChange={(e) => setForm(prev => ({ ...prev, price_cents: parseInt(e.target.value) || 0 }))}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 text-sm">
                      {formatPrice(form.price_cents)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  Stripe Product ID
                  <a
                    href="https://dashboard.stripe.com/products"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center ml-2 text-kod-accent hover:text-kod-accent-dark text-xs"
                  >
                    Open Stripe <ExternalLink size={10} className="ml-1" />
                  </a>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent font-mono text-sm"
                  placeholder="prod_..."
                  value={form.stripe_product_id}
                  onChange={(e) => setForm(prev => ({ ...prev, stripe_product_id: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  Stripe Price ID
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent font-mono text-sm"
                  placeholder="price_..."
                  value={form.stripe_price_id}
                  onChange={(e) => setForm(prev => ({ ...prev, stripe_price_id: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Tag (optional)</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                    placeholder="e.g., POPULAR"
                    value={form.tag}
                    onChange={(e) => setForm(prev => ({ ...prev, tag: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Sort Order</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                    value={form.sort_order}
                    onChange={(e) => setForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => setForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className="flex items-center gap-2"
                >
                  {form.is_active ? (
                    <ToggleRight size={24} className="text-green-400" />
                  ) : (
                    <ToggleLeft size={24} className="text-white/30" />
                  )}
                  <span className={`text-sm ${form.is_active ? 'text-green-400' : 'text-white/50'}`}>
                    {form.is_active ? 'Active' : 'Inactive'}
                  </span>
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mt-4 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowModal(false); setError(null); }}
                className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-4 py-2 rounded-lg text-white transition-colors ${
                  saving ? 'bg-kod-accent/50 cursor-not-allowed' : 'bg-kod-accent hover:bg-kod-accent-dark'
                }`}
              >
                {saving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : editingPackage ? 'Update Package' : 'Create Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-xl p-6 max-w-sm w-full shadow-2xl border border-kod-purple-600/50">
            <h3 className="text-lg font-bold mb-3">Delete Package</h3>
            <p className="text-white/70 text-sm mb-6">
              Are you sure you want to delete this coin package? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
